import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, Check, X, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { apiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Panel } from "@/pages/shared";

export const pwChecks = (pw) => ({
  length: pw.length >= 10,
  upper: /[A-Z]/.test(pw),
  lower: /[a-z]/.test(pw),
  digit: /\d/.test(pw),
  special: /[^A-Za-z0-9]/.test(pw),
});

function PwInput({ id, value, onChange, testid }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input id={id} type={show ? "text" : "password"} value={value} onChange={onChange} autoComplete="new-password" data-testid={testid} className="pr-10" />
      <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-slate-700" tabIndex={-1}>
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}

export default function Wachtwoord() {
  const { t } = useLang();
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [cur, setCur] = useState("");
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  const checks = pwChecks(pw1);
  const allOk = Object.values(checks).every(Boolean);
  const match = pw1.length > 0 && pw1 === pw2;
  const canSubmit = cur && allOk && match && !busy;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: pw1 });
      toast.success(t("pw_changed"));
      await refresh();
      setCur(""); setPw1(""); setPw2("");
      navigate("/");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "object" && d?.msg ? d.msg : apiError(d));
    } finally { setBusy(false); }
  };

  const rules = ["length", "upper", "lower", "digit", "special"];

  return (
    <div className="max-w-3xl mx-auto">
      <Panel title={t("change_password")} testid="password-panel">
        <form onSubmit={submit} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="space-y-4">
            {user?.must_change_password && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-sm px-3 py-2" data-testid="pw-forced-notice">{t("pw_first_login")}</div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="cur">{t("current_password")}</Label>
              <PwInput id="cur" value={cur} onChange={(e) => setCur(e.target.value)} testid="pw-current" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pw1">{t("new_password")}</Label>
              <PwInput id="pw1" value={pw1} onChange={(e) => setPw1(e.target.value)} testid="pw-new" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pw2">{t("repeat_password")}</Label>
              <PwInput id="pw2" value={pw2} onChange={(e) => setPw2(e.target.value)} testid="pw-repeat" />
            </div>
            <Button type="submit" disabled={!canSubmit} className="w-full" data-testid="pw-submit"><KeyRound className="h-4 w-4 mr-2" />{t("change_password")}</Button>
          </div>

          <div>
            <p className="text-sm font-semibold text-slate-700 mb-3">{t("pw_requirements")}</p>
            <ul className="space-y-2" data-testid="pw-checklist">
              {rules.map((r) => (
                <li key={r} className={`flex items-center gap-2 text-sm rounded-md px-3 py-2 transition-colors ${checks[r] ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-600"}`} data-testid={`pw-rule-${r}`} data-ok={checks[r]}>
                  {checks[r] ? <Check className="h-4 w-4" /> : <X className="h-4 w-4" />}{t(`pw_rule_${r}`)}
                </li>
              ))}
              <li className={`flex items-center gap-2 text-sm rounded-md px-3 py-2 transition-colors ${match ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-600"}`} data-testid="pw-rule-match" data-ok={match}>
                {match ? <Check className="h-4 w-4" /> : <X className="h-4 w-4" />}{t("pw_rule_match")}
              </li>
            </ul>
          </div>
        </form>
      </Panel>
    </div>
  );
}
